import { ErrorMessage, Stack } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { useEffect } from 'react'
import { trackCustomHeapEvent } from '@vend/utilities'
import { FulfilmentDetail } from '../../../../models/fulfilment'
import {
  HEAP_ADDRESS_NOT_VALIDATED_OBJECT,
  HEAP_ADDRESS_VALIDATED_OBJECT,
  HEAP_DELIVERY_SALE_ACTION,
  HEAP_NO_ADDRESS_OBJECT,
} from '../../sale-model.constant'
import { FEATURE_ENABLE_LRX_31079_FRONTEND_METRICS } from '../../../../constants'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { Addresses } from './address/Addresses'
import { EmptyAddress } from './address/EmptyAddress'
import {
  AddressErrorsStatus,
  getLineItemFulfillmentAddressDetails,
} from './popover/utils/fulfillmentHelper'

interface FulfilmentAddressProps {
  fulfillmentDetail: FulfilmentDetail | undefined
  isModalOpeningOnError?: boolean
  lineItemLocalId?: string
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

export const LineItemFulfilmentAddress: React.FC<FulfilmentAddressProps> = ({
  fulfillmentDetail,
  isModalOpeningOnError,
  lineItemLocalId,
  onSave,
}: FulfilmentAddressProps) => {
  const t = useT()
  const {
    isSameBillingAddress,
    formattedShippingAddress,
    formattedBillingAddress,
    isAddressEmpty,
    error,
    status,
  } = getLineItemFulfillmentAddressDetails(fulfillmentDetail)

  const shouldShowError =
    !!error &&
    (!isModalOpeningOnError ||
      status !== AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING)

  const noShippingAddress = !fulfillmentDetail || isAddressEmpty

  const HasBillingAddressInModal =
    isModalOpeningOnError &&
    (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)

  const showEmptyAddressButton = noShippingAddress && !HasBillingAddressInModal

  useEffect(() => {
    if (!getFeatureFromCache(FEATURE_ENABLE_LRX_31079_FRONTEND_METRICS)) return
    const objectName = showEmptyAddressButton
      ? HEAP_NO_ADDRESS_OBJECT
      : status === AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING
      ? HEAP_ADDRESS_VALIDATED_OBJECT
      : HEAP_ADDRESS_NOT_VALIDATED_OBJECT

    trackCustomHeapEvent({
      action: HEAP_DELIVERY_SALE_ACTION,
      object: objectName,
    })
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack space={4}>
      {showEmptyAddressButton ? (
        <EmptyAddress
          isModalOpeningOnError={isModalOpeningOnError}
          hasError={Boolean(error)}
          addressStatus={status}
          lineItemLocalId={lineItemLocalId}
          onSave={onSave}
        />
      ) : (
        <Addresses
          isSameBillingAddress={isSameBillingAddress}
          formattedShippingAddress={formattedShippingAddress}
          formattedBillingAddress={formattedBillingAddress}
          isModalOpeningOnError={isModalOpeningOnError}
          hasError={Boolean(error)}
          addressStatus={status}
          fulfilmentDetail={fulfillmentDetail}
          onSave={onSave}
          lineItemLocalId={lineItemLocalId}
        />
      )}
      {shouldShowError && (
        <ErrorMessage>{t(error, { _context: 'Alert text' })}</ErrorMessage>
      )}
    </Stack>
  )
}
